import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import AutocompleteInput from './AutocompleteInput';
import BaseButton from './BaseButton';
import OrderCategoryCard from './OrderCategoryCard';
import Loader from './Loader';
import ErrorModal from './Modal/ErrorModal';
import backendApi from '../services/backendApi';

function OrderForm({setRideInSearch, defaultValues = { addressFrom: null, addressTo: null }, defaultCategory = null }) {
    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        trigger,
        setValue
    } = useForm({ defaultValues });

    const addressFrom = watch('addressFrom');
    const addressTo = watch('addressTo');

    const [rideOptions, setRideOptions] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
    const [loading, setLoading] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const fetchAddressSuggestions = async (query) => {
        const response = await backendApi.get('/api/address', {
            params: { search: query },
        });
        return response.data.data || [];
    };

    const onSubmit = async (data) => {
        setLoading(true);
        setErrorModalOpen(false);
        setErrorMessage(null);

        try {
            const response = await backendApi.post('/api/search-ride', JSON.stringify(data), {
                headers: { 'Content-Type': 'application/json' },
            });

            const result = response.data;

            if (!result.success) {
                setErrorModalOpen(true);
                setErrorMessage('Nepavyko rasti automobilio');
            } else {
                setRideOptions(result.categories || []);
            }
        } catch (error) {
            setErrorModalOpen(true);
            setErrorMessage('Įvyko klaida ieškant automobilio');
        } finally {
            setLoading(false);
        }
    };

    const createNewOrder = async () => {
        setLoading(true);
        setErrorModalOpen(false);
        setErrorMessage(null);

        const values = getValues();

        if (!selectedCategory || !values.addressFrom || !values.addressTo) return;

        const payload = {
            addressFrom: values.addressFrom,
            addressTo: values.addressTo,
            categoryId: selectedCategory.id,
        };

        try {
            const response = await backendApi.post('/api/create-ride', JSON.stringify(payload), {
                headers: { 'Content-Type': 'application/json' },
            });

            console.log(response);

            if (!response.data.success) {
                setErrorModalOpen(true);
                setErrorMessage('Nepavyko sukurti užsakymo');
            } else {
                setRideInSearch(true);
            }
        } catch (error) {
            console.log(error);
            setErrorModalOpen(true);
            setErrorMessage('Įvyko klaida kuriant užsakymą');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeout = setTimeout(async () => {
            const valid = await trigger(['addressFrom', 'addressTo']);
            const isBothObjects = typeof addressFrom === 'object' && typeof addressTo === 'object';

            if (valid && isBothObjects) {
                handleSubmit(onSubmit)();
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [addressFrom, addressTo]);

    useEffect(() => {
        const isInvalid =
            !addressFrom || typeof addressFrom !== 'object' ||
            !addressTo || typeof addressTo !== 'object';

        if (isInvalid) {
            setRideOptions([]);
            setSelectedCategory(null);
        }
    }, [addressFrom, addressTo]);

    if (loading) return <Loader />;
    if (errorModalOpen) return <ErrorModal message={errorMessage} onClose={() => setErrorModalOpen(false)} />;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="formWrapper">
            <Controller
                name="addressFrom"
                control={control}
                rules={{ required: "Kelionės pradžia yra privaloma" }}
                render={({ field, fieldState }) => (
                    <AutocompleteInput
                        {...field}
                        label="Kelionės pradžia"
                        fetchSuggestions={fetchAddressSuggestions}
                        error={fieldState.error}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (typeof val === "string") {
                                setRideOptions([]);
                                setSelectedCategory(null);
                            } else {
                                field.onChange(e);
                            }
                        }}
                    />
                )}
            />

            <Controller
                name="addressTo"
                control={control}
                rules={{ required: "Kelionės tikslas yra privalomas" }}
                render={({ field, fieldState }) => (
                    <AutocompleteInput
                        {...field}
                        label="Kelionės tikslas"
                        fetchSuggestions={fetchAddressSuggestions}
                        error={fieldState.error}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (typeof val === "string") {
                                setRideOptions([]);
                                setSelectedCategory(null);
                            } else {
                                field.onChange(e);
                            }
                        }}
                    />
                )}
            />

            {rideOptions.length > 0 && (
                <div className="ride-options-list">
                    {rideOptions.map((option) => (
                        <OrderCategoryCard
                            key={option.id}
                            name={option.name}
                            description={option.description}
                            price={option.price}
                            passengers={option.passengers}
                            logo={option.logo}
                            isSelected={selectedCategory?.id === option.id}
                            onSelect={() => setSelectedCategory(option)}
                        />
                    ))}

                    {selectedCategory && (
                        <div className="order__buttonWrapper">
                            <BaseButton
                                text={`Užsisakyti`}
                                handleOnClick={createNewOrder}
                                className="baseButton--primary"
                            />
                        </div>
                    )}
                </div>
            )}
        </form>
    );
}

export default OrderForm;